import React from 'react'

/**
 * CSS Modifier suffixes for the optional CTA button rendered on top of a linked
 * media block. Keys mirror the values the admin panel's media block editor saves
 * (`buttonSize` / `buttonShape` / `buttonPosition`), so any value that comes back
 * from the API maps directly onto a class here — see blog-components.scss for the
 * matching `.media-cta-btn--*` rules.
 */
const BUTTON_SIZE_CLASSES = {
    sm: 'media-cta-btn--sm',
    md: 'media-cta-btn--md',
    lg: 'media-cta-btn--lg',
}

const BUTTON_SHAPE_CLASSES = {
    square: 'media-cta-btn--square',
    rounded: 'media-cta-btn--rounded',
}

const BUTTON_POSITION_CLASSES = {
    top: 'media-cta-btn--top',
    bottom: 'media-cta-btn--bottom',
    left: 'media-cta-btn--left',
    right: 'media-cta-btn--right',
    center: 'media-cta-btn--center',
    'top-left': 'media-cta-btn--top-left',
    'top-right': 'media-cta-btn--top-right',
    'bottom-left': 'media-cta-btn--bottom-left',
    'bottom-right': 'media-cta-btn--bottom-right',
}

const DEFAULT_BUTTON_TEXT = 'Learn More'

/**
 * Media component for displaying single images or videos
 * @param {Object} props - Component props
 * @param {Object} props.data - Data containing file object
 * @returns {JSX.Element}
 */
const Media = ({ data }) => {
    if (!data) {
        return null
    }

    // New API structure: url and alt are direct properties
    // Strapi structure: url and alt (alternativeText) are nested in file object
    const fileUrl = data.url || data.file?.url
    const altText = data.alt || data.file?.alternativeText || 'Media content'

    if (!fileUrl) {
        return null
    }

    // Check if it's a video
    const isVideo = data.mime?.startsWith('video/') || data.file?.mime?.startsWith('video/')

    // Optional CTA link, same feature the admin panel's media block editor configures.
    // Only applies to images — matches the admin preview, which only ever wraps
    // `<img>` in a link. The on-image button is a further opt-in on top of that
    // (`showButton`, default false) — a link alone still makes the image clickable
    // without growing a button nobody asked for.
    const link = data.link
    const isLinked = !isVideo && !!link
    const hasButton = isLinked && !!data.showButton

    return (
        <div className="blog-media">
            {isVideo ? (
                <video
                    src={fileUrl}
                    controls
                    className="media-video"
                >
                    Your browser does not support the video tag.
                </video>
            ) : isLinked ? (
                <a
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="media-image-link"
                >
                    <img
                        src={fileUrl}
                        alt={altText}
                        className="media-image"
                    />
                    {hasButton ? (
                        <span
                            className={[
                                'media-cta-btn',
                                BUTTON_SIZE_CLASSES[data.buttonSize] || BUTTON_SIZE_CLASSES.md,
                                BUTTON_SHAPE_CLASSES[data.buttonShape] || BUTTON_SHAPE_CLASSES.square,
                                BUTTON_POSITION_CLASSES[data.buttonPosition] || BUTTON_POSITION_CLASSES.bottom,
                            ].join(' ')}
                            aria-hidden="true"
                        >
                            {data.buttonText?.trim() || DEFAULT_BUTTON_TEXT}
                        </span>
                    ) : null}
                </a>
            ) : (
                <img
                    src={fileUrl}
                    alt={altText}
                    className="media-image"
                />
            )}
        </div>
    )
}

export default Media
